import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { leaveService } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectOption } from '../components/ui/select';
import AppLayout from '../layouts/AppLayout';

const LeaveRequestsPage = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Check for success message in navigation state
  useEffect(() => {
    if (location.state?.success) {
      setSuccessMessage(location.state.success);
      // Clear the success message from location state after displaying
      window.history.replaceState({}, document.title);
      
      // Trigger a dashboard update when we get a success message
      // (typically happens after a leave request action)
      window.dispatchEvent(new Event('leave-request-updated'));
    }
  }, [location.state]);
  
  // Fetch leave requests
  const fetchLeaveRequests = async () => {
    try {
      setLoading(true);
      let response;
      
      if (user.role === 'admin' || user.role === 'manager') {
        response = await leaveService.getAllLeaveRequests();
      } else {
        response = await leaveService.getUserLeaveRequests();
      }
      
      setLeaveRequests(response.leaveRequests || []);
      setFilteredRequests(response.leaveRequests || []);
      setLoading(false);
      
      // Notify dashboard about the data update
      window.dispatchEvent(new Event('leave-request-updated'));
      
      return response.leaveRequests || [];
    } catch (err) {
      console.error('Error fetching leave requests:', err);
      setError('Failed to load leave requests. Please try again.');
      setLoading(false);
      return [];
    }
  };

  useEffect(() => {
    fetchLeaveRequests();
  }, [user.role]);
  
  // Apply status filter
  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredRequests(leaveRequests);
    } else {
      const filtered = leaveRequests.filter(request => request.status === statusFilter);
      setFilteredRequests(filtered);
    }
  }, [statusFilter, leaveRequests]);
  
  // Handle filter change
  const handleFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };
  
  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Check if request is half-day
  const isHalfDayRequest = (request) => {
    // First check: explicit request type
    const hasHalfDayType = request.request_type && request.request_type !== 'full-day';
    
    // Second check: working days is exactly 0.5
    const hasHalfDayDuration = request.working_days !== undefined && 
                               request.working_days !== null && 
                               parseFloat(request.working_days) === 0.5;
    
    // Third check: same start/end date (potential half-day)
    const isSameDate = request.start_date === request.end_date;
    
    // Return true if ANY of these conditions are met
    return hasHalfDayType || hasHalfDayDuration || (isSameDate && hasHalfDayDuration);
  };
  
  // UPDATED: Better duration calculation for half-days
  const formatDuration = (request) => {
    // Use working_days first if available (most reliable)
    if (request.working_days !== undefined && request.working_days !== null) {
      const days = parseFloat(request.working_days);
      
      // If it's exactly 0.5, it's definitely a half-day
      if (days === 0.5) {
        const halfDayType = request.request_type ? 
          request.request_type.replace('-', ' ') : 'half-day';
        return `0.5 days (${halfDayType})`;
      }
      
      // For other decimal values or whole numbers
      return `${days} ${days === 1 ? 'day' : 'days'}`;
    }
    
    // Check if request type indicates half-day
    const isHalfDay = isHalfDayRequest(request);
    if (isHalfDay && request.request_type && request.request_type !== 'full-day') {
      const halfDayType = request.request_type.replace('-', ' ');
      return `0.5 days (${halfDayType})`;
    }
    
    // Fallback calculation for older full-day requests
    const calculateDuration = (startDate, endDate) => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    };
    
    return `${calculateDuration(request.start_date, request.end_date)} days`;
  };
  
  // UPDATED: Better date range display for half-days  
  const formatDateRange = (request) => {
    const startFormatted = formatDate(request.start_date);
    const endFormatted = formatDate(request.end_date);
    
    // Check if it's a half-day using working_days first
    const isHalfDayByDuration = request.working_days !== undefined && 
                                request.working_days !== null && 
                                parseFloat(request.working_days) === 0.5;
    
    const isHalfDayByType = request.request_type && request.request_type !== 'full-day';
    
    const isHalfDay = isHalfDayByDuration || isHalfDayByType;
    
    // If it's a half-day, show single date with type
    if (isHalfDay || request.start_date === request.end_date) {
      const halfDayType = request.request_type && request.request_type !== 'full-day' ? 
        ` (${request.request_type.replace('-', ' ')})` : '';
      return `${startFormatted}${halfDayType}`;
    }
    
    return `${startFormatted} - ${endFormatted}`;
  };
  
  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Handle manual refresh
  const handleRefresh = () => {
    fetchLeaveRequests();
  };
  
  if (loading) {
    return (
      <AppLayout>
        <div className="text-center py-10">Loading leave requests...</div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Leave Requests</h1>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleRefresh}>
              Refresh Data
            </Button>
            <Link to="/leave-requests/new">
              <Button>Request Leave</Button>
            </Link>
          </div>
        </div>
        
        {/* Success message */}
        {successMessage && (
          <div className="bg-green-50 text-green-700 p-4 rounded-md">
            {successMessage}
          </div>
        )}
        
        {/* Error message */}
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-md">
            {error}
          </div>
        )}
        
        {/* Filters */}
        <Card className="p-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium">Filter by status:</label>
            <Select
              value={statusFilter}
              onChange={handleFilterChange}
              className="w-40"
            >
              <SelectOption value="all">All Requests</SelectOption>
              <SelectOption value="pending">Pending</SelectOption>
              <SelectOption value="approved">Approved</SelectOption>
              <SelectOption value="rejected">Rejected</SelectOption>
            </Select>
          </div>
        </Card>
        
        {/* Leave requests list */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>
              {statusFilter === 'all' ? 'All Leave Requests' : `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Requests`}
            </CardTitle>
            <div className="text-sm text-gray-500">
              {filteredRequests.length} {filteredRequests.length === 1 ? 'request' : 'requests'} found
            </div>
          </CardHeader>
          <CardContent>
            {filteredRequests.length > 0 ? (
              <div className="space-y-4">
                {filteredRequests.map((request) => {
                  const isHalfDay = isHalfDayRequest(request);
                  
                  return (
                    <div 
                      key={request.id} 
                      className={`p-4 border rounded-lg hover:bg-gray-50 transition-colors ${
                        isHalfDay ? 'border-l-4 border-l-blue-400 bg-blue-50/30' : ''
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:justify-between md:items-center">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <h3 className="font-medium text-lg">
                              {user.role === 'admin' || user.role === 'manager' 
                                ? request.user_name 
                                : request.leave_type || request.leave_type_name}
                            </h3>
                            <span 
                              className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(request.status)}`}
                            >
                              {request.status}
                            </span>
                            {/* Half-day indicator */}
                            {isHalfDay && (
                              <span className="ml-2 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">
                                🕐 Half-day
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div>
                              <p className="text-sm text-gray-500">Date Range</p>
                              <p className={`text-sm ${isHalfDay ? 'text-blue-700 font-medium' : ''}`}>
                                {formatDateRange(request)}
                              </p>
                            </div>
                            
                            <div>
                              <p className="text-sm text-gray-500">Duration</p>
                              <div className="flex items-center">
                                <p className={`text-sm ${isHalfDay ? 'text-blue-700 font-medium' : ''}`}>
                                  {formatDuration(request)}
                                </p>
                                {isHalfDay && (
                                  <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded">
                                    -0.5 days
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {user.role === 'admin' || user.role === 'manager' ? (
                              <div>
                                <p className="text-sm text-gray-500">Leave Type</p>
                                <div className="flex items-center">
                                  <p className="text-sm">{request.leave_type || request.leave_type_name}</p>
                                  {isHalfDay && request.request_type && (
                                    <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                                      {request.request_type === 'morning-half' ? '🌅 AM' : '🌆 PM'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <p className="text-sm text-gray-500">
                                  {request.status === 'approved' ? 'Approved By' : 
                                   request.status === 'rejected' ? 'Rejected By' : 'Pending Review'}
                                </p>
                                <p className="text-sm">
                                  {/* Fix: Check for both field names since the backend uses different field names */}
                                  {request.status === 'pending' 
                                    ? 'Pending' 
                                    : (request.approver_name || request.reviewer_name || 'Unknown')}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {/* Enhanced display for half-day request type */}
                          {isHalfDay && request.request_type && (
                            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                              <p className="text-xs text-blue-700">
                                <span className="font-medium">Request Type:</span> {request.request_type.replace('-', ' ').charAt(0).toUpperCase() + request.request_type.replace('-', ' ').slice(1)}
                                {request.request_type === 'morning-half' && ' (AM)'}
                                {request.request_type === 'afternoon-half' && ' (PM)'}
                              </p>
                            </div>
                          )}
                          
                          {request.reason && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-500">Reason</p>
                              <p className="text-sm">{request.reason}</p>
                            </div>
                          )}
                          
                          {request.comment && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-500">
                                {request.status === 'approved' ? 'Approval Comment' : 'Rejection Reason'}
                              </p>
                              <p className="text-sm">{request.comment}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="mt-3 md:mt-0 md:ml-4 flex justify-end">
                          {(user.role === 'admin' || user.role === 'manager') && request.status === 'pending' && (
                            <Link to={`/leave-requests/review/${request.id}`}>
                              <Button 
                                size="sm"
                                className={isHalfDay ? 'bg-blue-600 hover:bg-blue-700' : ''}
                              >
                                Review {isHalfDay ? 'Half-day' : ''}
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">
                No leave requests found
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default LeaveRequestsPage;